import {
  Box,
  Button,
  Card,
  CardHeader,
  Checkbox,
  CircularProgress,
  Collapse,
  FormControlLabel,
  Grid,
  makeStyles,
  TextField,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import SearchIcon from '@material-ui/icons/Search';
import { Alert, Autocomplete } from '@material-ui/lab';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { EmptyView } from 'components';
import { CustomTooltip } from 'components/CustomTooltip';
import {
  getCandidateLookup,
  getCandidateSearchLookup,
  searchCandidateVacancies,
  submitCandidateVacancyFeedback,
  logResultClick,
} from 'lib/Api/SimilarCandidates';
import { getUserData } from 'lib/Helper/Auth';
import {
  AppliedVacancy,
  CandidateLookupItem,
  CandidateVacanciesSearchFilters,
  CandidateVacancyFeedbackPayload,
  CandidateVacancyResult,
  RatedBadVacancy,
  SimilarCandidateFeedbackAction,
  SimilarCandidatesTarget,
} from 'lib/Model/SimilarCandidates';
import { toMonetary } from 'lib/Utility';
import * as React from 'react';
import { Feedback } from 'views/SimilarCandidates/components/FeedbackControls';
import { WEAK_LLM_SCORE } from 'views/SimilarCandidates/components/ResultsList';
import { TargetCandidateCard } from 'views/SimilarCandidates/components/TargetCandidateCard';
import { GuideStep, MatcherTutorial } from 'views/VacancyMatcher/MatcherGuide';
import { VacancySyncNote } from 'views/VacancyMatcher/VacancySyncNote';
import { VacancyResultCard } from './components/VacancyResultCard';

const permission = 'vacancy_matcher';
const maxResults = 20;
// the rest is one click away
const initialVisible = 10;
const focusMaxChars = 200;
// an id or an ats url goes straight to the search, anything else searches candidates by name
const candidateIdPattern = /^\d+$|\/candidates?\/\d+/i;
// nothing reaching this rerank score = no close fit among the open vacancies
const weakSetLlmThreshold = 0.7;
// regions.name and work_locations.name in the ats, the api filters on these exact names
const regionOptions = [
  { name: 'Central Malta', country: 'Malta' },
  { name: 'North of Malta', country: 'Malta' },
  { name: 'South of Malta', country: 'Malta' },
  { name: 'Gozo', country: 'Malta' },
  { name: 'Nicosia', country: 'Cyprus' },
  { name: 'Limassol', country: 'Cyprus' },
  { name: 'Larnaca', country: 'Cyprus' },
  { name: 'Paphos', country: 'Cyprus' },
  { name: 'Famagusta', country: 'Cyprus' },
  { name: 'Kyrenia', country: 'Cyprus' },
];
const workLocationOptions = ['Fully Remote', 'Hybrid', 'On Premises'];
// employment_types.title for konnekt (client 2), same deal
const employmentTypeOptions = [
  'Full Time',
  'Part Time',
  'Temporary / Contract',
  'Internship',
  'Shift',
  'Remote',
];

type SearchState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'error'; message: string; help?: string; code?: string }
  | {
      kind: 'loaded';
      searchId: string;
      candidate: SimilarCandidatesTarget;
      results: CandidateVacancyResult[];
      vacanciesConsidered: number;
      eligibilityDropped: number;
      rerankError?: string | null;
      considerSalary: boolean;
      salaryComparedTo?: number | null;
      salaryFlagged: number;
      considerJobFocus: boolean;
      jobFocusPreferences: string[];
      regions: string[];
      workLocations: string[];
      alreadyApplied: AppliedVacancy[];
      hiddenRatedBad: RatedBadVacancy[];
    };

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(2),
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap',
  },
  field: {
    flex: 1,
    minWidth: 280,
  },
  searchButton: {
    height: 40,
  },
  zone: {
    marginTop: theme.spacing(3),
  },
  sectionHeader: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 1,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  loadingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  results: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(2),
    alignItems: 'stretch',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  showMoreRow: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(1.5),
  },
}));

// the API sends { message, help, code } for known errors, a string otherwise
function getSearchError(error: unknown): {
  message: string;
  help?: string;
  code?: string;
} {
  const response = (
    error as { response?: { status?: number; data?: { detail?: unknown } } }
  )?.response;
  const detail = response?.data?.detail as
    | { message?: unknown; help?: unknown; code?: unknown }
    | string
    | undefined;

  if (
    detail &&
    typeof detail === 'object' &&
    typeof detail.message === 'string'
  ) {
    const known: { message: string; help?: string; code?: string } = {
      message: detail.message,
    };
    if (typeof detail.help === 'string') {
      known.help = detail.help;
    }
    if (typeof detail.code === 'string') {
      known.code = detail.code;
    }
    return known;
  }
  if (typeof detail === 'string' && detail.trim() !== '') {
    return { message: detail };
  }
  let status = '';
  if (response?.status) {
    status = ` (${response.status})`;
  }
  return {
    message: `Search failed${status}.`,
  };
}

interface Props {
  // set by the matcher page right after its intro closes
  startGuide?: boolean;
  onGuideStarted?(): void;
}

const CandidateVacanciesView: React.FC<Props> = ({
  startGuide,
  onGuideStarted,
}) => {
  const classes = useStyles();
  const [input, setInput] = React.useState('');
  const [lookupOptions, setLookupOptions] = React.useState<
    CandidateLookupItem[]
  >([]);
  const [lookupLoading, setLookupLoading] = React.useState(false);
  // who the pasted input resolves to, so the recruiter knows it's the right candidate
  const [inputMatch, setInputMatch] = React.useState<{
    input: string;
    label: string | null;
  } | null>(null);
  const [focusText, setFocusText] = React.useState('');
  const [publishedOnly, setPublishedOnly] = React.useState(false);
  const [considerSalary, setConsiderSalary] = React.useState(true);
  const [considerJobFocus, setConsiderJobFocus] = React.useState(false);
  const [regions, setRegions] = React.useState<string[]>([]);
  const [workLocations, setWorkLocations] =
    React.useState<string[]>(workLocationOptions);
  const [employmentTypes, setEmploymentTypes] = React.useState<string[]>(
    employmentTypeOptions,
  );
  const [search, setSearch] = React.useState<SearchState>({ kind: 'idle' });
  const [showAll, setShowAll] = React.useState(false);
  // published only and the pjf ranking sit behind "More options", recruiters rarely change them
  const [showMoreOptions, setShowMoreOptions] = React.useState(false);
  const [showApplied, setShowApplied] = React.useState(false);
  const [showRatedBad, setShowRatedBad] = React.useState(false);
  const [feedback, setFeedback] = React.useState<
    Record<string, Feedback | undefined>
  >({});
  const requestId = React.useRef(0);
  const [guideOpen, setGuideOpen] = React.useState(false);
  const searchRef = React.useRef<HTMLDivElement>(null);
  const preferencesRef = React.useRef<HTMLDivElement>(null);
  const resultsRef = React.useRef<HTMLDivElement>(null);
  // memo so the guide doesn't re-scroll every time this view re-renders
  const guideSteps = React.useMemo<GuideStep[]>(
    () => [
      {
        title: 'Start with a candidate',
        body: 'Search by name or email, or paste an ATS candidate URL or ID. A candidate created or a CV uploaded today shows up from tomorrow.',
        target: searchRef,
      },
      {
        title: "Set the candidate's preferences",
        body: 'Pick the regions, work arrangements and employment types the candidate is open to. Use Other preferences for anything else, like no weekend work.',
        target: preferencesRef,
      },
      {
        title: 'Read the results',
        body: 'Each vacancy has a match score and a short reason. Vacancies the candidate already applied for are left out and listed separately. Mark matches good or bad as you review them.',
        target: resultsRef,
      },
    ],
    [],
  );

  const hasPermission = getUserData()?.permissions?.includes(permission);

  React.useEffect(() => {
    if (!startGuide) return;
    setGuideOpen(true);
    onGuideStarted?.();
  }, [startGuide, onGuideStarted]);

  // same debounce as the vacancy prefill
  React.useEffect(() => {
    const candidate = input.trim();
    if (!candidateIdPattern.test(candidate)) return undefined;

    let cancelled = false;
    const handle = window.setTimeout(() => {
      getCandidateLookup(candidate)
        .then((found) => {
          if (cancelled) return;
          let label: string | null = null;
          if (found.candidate_id) {
            const name = [found.first_name, found.last_name]
              .filter(Boolean)
              .join(' ');
            label = name || `Candidate ${found.candidate_id}`;
          }
          setInputMatch({ input: candidate, label });
        })
        .catch(() => undefined);
    }, 500);

    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [input]);

  // the candidate dropdown, empty box = who they searched lately
  React.useEffect(() => {
    const q = input.trim();
    // a name search under 3 letters scans too many candidates
    if (candidateIdPattern.test(q) || (q && q.length < 3)) {
      setLookupOptions([]);
      return undefined;
    }

    let cancelled = false;
    let delay = 0;
    if (q) delay = 300;
    const handle = window.setTimeout(() => {
      setLookupLoading(true);
      getCandidateSearchLookup(q)
        .then((rows) => {
          if (!cancelled) setLookupOptions(rows);
        })
        .catch(() => {
          if (!cancelled) setLookupOptions([]);
        })
        .finally(() => {
          if (!cancelled) setLookupLoading(false);
        });
    }, delay);

    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [input]);

  let inputHelper: string | undefined;
  let inputNotFound = false;
  if (inputMatch && inputMatch.input === input.trim()) {
    if (inputMatch.label) {
      inputHelper = `Candidate: ${inputMatch.label}`;
    } else {
      inputHelper = 'No candidate found for this input.';
      inputNotFound = true;
    }
  }

  async function runSearch(force = false) {
    const candidate = input.trim();
    if (!candidateIdPattern.test(candidate)) return;

    const activeRequest = requestId.current + 1;
    requestId.current = activeRequest;
    setFeedback({});
    setShowAll(false);
    setSearch({ kind: 'loading' });

    try {
      const filters: CandidateVacanciesSearchFilters = {
        published_only: publishedOnly,
        consider_salary: considerSalary,
        consider_job_focus: considerJobFocus,
      };
      if (regions.length > 0) {
        filters.regions = regions;
      }
      // all ticked = no filter
      if (workLocations.length < workLocationOptions.length) {
        filters.work_locations = workLocations;
      }
      if (employmentTypes.length < employmentTypeOptions.length) {
        filters.employment_types = employmentTypes;
      }
      if (force) {
        filters.force = true;
      }
      if (focusText.trim()) {
        filters.focus = focusText.trim();
      }
      const response = await searchCandidateVacancies(candidate, filters);
      if (requestId.current !== activeRequest) return;

      const results = [...response.results]
        .sort((a, b) => b.final_score - a.final_score)
        .slice(0, maxResults);
      setSearch({
        kind: 'loaded',
        searchId: response.search_id,
        candidate: response.target,
        results,
        vacanciesConsidered: response.vacancies_considered,
        eligibilityDropped: response.eligibility_dropped ?? 0,
        rerankError: response.rerank_error,
        considerSalary,
        salaryComparedTo: response.salary_compared_to,
        salaryFlagged: response.salary_flagged ?? 0,
        considerJobFocus,
        jobFocusPreferences: response.job_focus_preferences ?? [],
        regions,
        workLocations,
        alreadyApplied: response.already_applied ?? [],
        hiddenRatedBad: response.hidden_rated_bad ?? [],
      });
      // the hidden ones come back already marked bad, undo on the card brings them back next search
      const marked: Record<string, Feedback | undefined> = {};
      for (const hidden of response.hidden_rated_bad ?? []) {
        marked[hidden.vacancy_id] = {
          kind: 'bad',
          reasons: hidden.reasons,
          note: hidden.note ?? undefined,
        };
      }
      setFeedback(marked);
    } catch (error) {
      if (requestId.current !== activeRequest) return;
      setSearch({ kind: 'error', ...getSearchError(error) });
    }
  }

  function handleResultOpen(id: string) {
    if (search.kind !== 'loaded') return;
    const rank = search.results.findIndex((r) => r.vacancy_id === id) + 1;
    if (rank === 0) return;
    logResultClick('candidate_vacancy_click', {
      search_id: search.searchId,
      result_id: id,
      rank,
    });
  }

  function handleFeedbackChange(id: string, next: Feedback | undefined) {
    setFeedback((prev) => ({ ...prev, [id]: next }));
    if (search.kind !== 'loaded') return;

    let rank: number | null =
      search.results.findIndex((r) => r.vacancy_id === id) + 1;
    let result: CandidateVacancyResult | undefined = search.results[rank - 1];
    // or one of the hidden rated-bad ones, unranked
    if (!result) {
      rank = null;
      result = search.hiddenRatedBad.find((r) => r.vacancy_id === id);
    }
    if (!result) return;
    let action: SimilarCandidateFeedbackAction = 'cleared';
    if (next !== undefined) {
      action = next.kind;
    }
    const payload: CandidateVacancyFeedbackPayload = {
      search_id: search.searchId,
      action,
      result,
      candidate_id: search.candidate.candidate_id,
      rank,
    };
    if (next?.kind === 'bad') {
      payload.reasons = next.reasons;
      payload.note = next.note;
    }
    // best effort, never get in the recruiter's way
    submitCandidateVacancyFeedback(payload).catch(() => undefined);
  }

  if (!hasPermission) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <Typography variant="h6" color="error">
          You do not have permission to access this feature.
        </Typography>
      </Box>
    );
  }

  const rerankRan =
    search.kind === 'loaded' && search.results.some((r) => r.llm_score != null);
  const rerankFailed =
    search.kind === 'loaded' && search.results.length > 0 && !rerankRan;
  const weakSet =
    search.kind === 'loaded' &&
    rerankRan &&
    !search.results.some((r) => (r.llm_score ?? 0) >= weakSetLlmThreshold);

  let errorSeverity: 'warning' | 'info' = 'info';
  if (search.kind === 'error' && search.code === 'target_profile_thin') {
    errorSeverity = 'warning';
  }

  let hiddenOptionsOn = 0;
  if (publishedOnly) hiddenOptionsOn += 1;
  if (considerJobFocus) hiddenOptionsOn += 1;
  let moreOptionsLabel = 'More options';
  if (showMoreOptions) {
    moreOptionsLabel = 'Fewer options';
  } else if (hiddenOptionsOn > 0) {
    moreOptionsLabel = `More options (${hiddenOptionsOn} on)`;
  }

  let resultsSummary = '';
  let visible: CandidateVacancyResult[] = [];
  let hiddenCount = 0;
  let mostPayBelow = false;
  if (search.kind === 'loaded') {
    resultsSummary = `Top ${
      search.results.length
    } of ${search.vacanciesConsidered.toLocaleString()} open vacancies.`;
    if (search.eligibilityDropped > 0) {
      resultsSummary += ` ${search.eligibilityDropped} EU-only ones left out.`;
    }
    if (search.salaryComparedTo == null && search.considerSalary) {
      resultsSummary +=
        " No salary for this candidate on the ATS, so salary wasn't checked.";
    }
    if (search.considerJobFocus && search.jobFocusPreferences.length === 0) {
      resultsSummary +=
        ' No preferred job areas for this candidate on the ATS.';
    }
    visible = search.results;
    if (!showAll) {
      // weak vacancies come without a reason, so they stay behind "Show more"
      visible = search.results
        .slice(0, initialVisible)
        .filter((r) => r.llm_score == null || r.llm_score > WEAK_LLM_SCORE);
    }
    hiddenCount = search.results.length - visible.length;
    mostPayBelow =
      visible.length > 0 &&
      visible.filter((r) => r.salary_below).length * 2 > visible.length;
    // the warning says it when it's most of them, otherwise one short line
    if (
      search.salaryComparedTo != null &&
      !mostPayBelow &&
      search.salaryFlagged > 0
    ) {
      resultsSummary += ` ${
        search.salaryFlagged
      } pay below the candidate's ${toMonetary(search.salaryComparedTo)}.`;
    }
  }

  return (
    <Grid container>
      <Grid item xs={12}>
        <Card className={classes.card}>
          <CardHeader
            title="Find Vacancies"
            subheader={
              <>
                Find the open vacancies that best fit a candidate.
                <VacancySyncNote />
              </>
            }
            action={
              <Button
                onClick={() => setGuideOpen(true)}
                variant="outlined"
                size="small"
                startIcon={<HelpOutlineIcon />}
              >
                Show guide
              </Button>
            }
          />

          <Box px={2}>
            {/* top, not centre: the name under the input would push the button down */}
            <div
              ref={searchRef}
              className={classes.row}
              style={{ alignItems: 'flex-start' }}
            >
              <Autocomplete<CandidateLookupItem, false, false, true>
                className={classes.field}
                freeSolo
                openOnFocus
                autoHighlight
                options={lookupOptions}
                loading={lookupLoading}
                // the server already filtered, don't let mui filter again on the label
                filterOptions={(options) => options}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return option.candidate_id;
                }}
                groupBy={
                  input.trim() ? undefined : () => 'Your recent searches'
                }
                value={null}
                inputValue={input}
                onInputChange={(e, value, reason) => {
                  // mui resets the text after a pick, we set it ourselves in onChange
                  if (reason === 'reset') return;
                  setInput(value);
                }}
                onChange={(e, picked) => {
                  if (picked && typeof picked !== 'string') {
                    setInput(picked.candidate_id);
                  }
                }}
                renderOption={(option) => {
                  const name = [option.first_name, option.last_name]
                    .filter(Boolean)
                    .join(' ');
                  const details = [];
                  if (option.email_address) details.push(option.email_address);
                  details.push(`#${option.candidate_id}`);
                  if (option.last_searched) {
                    details.push(
                      `searched ${formatDistanceToNow(
                        parseISO(option.last_searched),
                      )} ago`,
                    );
                  } else if (option.modified) {
                    details.push(
                      `updated ${formatDistanceToNow(
                        parseISO(option.modified),
                      )} ago`,
                    );
                  }
                  return (
                    <Box width="100%">
                      <Typography variant="body2" style={{ fontWeight: 500 }}>
                        {name || `Candidate ${option.candidate_id}`}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {details.join(' · ')}
                      </Typography>
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    label="Candidate"
                    placeholder="Search by name or email, or paste an ATS URL or ID"
                    InputLabelProps={{
                      ...params.InputLabelProps,
                      shrink: true,
                    }}
                    helperText={inputHelper}
                    error={inputNotFound}
                    onKeyDown={(e) => {
                      // words pick from the dropdown instead
                      if (
                        e.key === 'Enter' &&
                        candidateIdPattern.test(input.trim())
                      )
                        runSearch();
                    }}
                  />
                )}
              />
              <Button
                className={classes.searchButton}
                variant="contained"
                color="primary"
                startIcon={<SearchIcon />}
                disabled={
                  search.kind === 'loading' ||
                  !candidateIdPattern.test(input.trim())
                }
                onClick={() => runSearch()}
              >
                Search
              </Button>
            </div>
            <div ref={preferencesRef}>
              <Box className={classes.row} mt={1}>
                <Autocomplete
                  className={classes.field}
                  multiple
                  size="small"
                  options={regionOptions}
                  groupBy={(option) => option.country}
                  getOptionLabel={(option) => option.name}
                  value={regionOptions.filter((r) => regions.includes(r.name))}
                  onChange={(e, picked) =>
                    setRegions(picked.map((r) => r.name))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Candidate's preferred regions"
                      placeholder={regions.length === 0 ? 'All regions' : ''}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
                <TextField
                  className={classes.field}
                  variant="outlined"
                  size="small"
                  label="Other preferences (optional)"
                  placeholder='e.g. "no weekend work"'
                  InputLabelProps={{ shrink: true }}
                  value={focusText}
                  onChange={(e) =>
                    setFocusText(e.currentTarget.value.slice(0, focusMaxChars))
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') runSearch();
                  }}
                />
              </Box>
              {/* flex so the tooltip trigger, a block div, stays on the line with its checkboxes */}
              <Box mt={1} display="flex" alignItems="center" flexWrap="wrap">
                <Typography
                  variant="body2"
                  component="span"
                  style={{ display: 'flex', marginRight: 8 }}
                >
                  <CustomTooltip
                    titleHeader="Regions and work arrangement"
                    bodyText="Only vacancies in the chosen regions and arrangements are compared. Fully remote vacancies always show whatever regions you pick, and vacancies with no region or arrangement stated always show too."
                    placement="bottom"
                    maxWidth={320}
                    useDefaultUnderline
                  >
                    Work arrangement
                  </CustomTooltip>
                  :
                </Typography>
                {workLocationOptions.map((option) => (
                  <FormControlLabel
                    key={option}
                    style={{ marginLeft: 8 }}
                    control={
                      <Checkbox
                        size="small"
                        color="primary"
                        checked={workLocations.includes(option)}
                        // keep at least one, none ticked would show nothing
                        disabled={
                          workLocations.length === 1 &&
                          workLocations.includes(option)
                        }
                        onChange={(e) =>
                          setWorkLocations(
                            e.target.checked
                              ? workLocationOptions.filter(
                                  (o) =>
                                    o === option || workLocations.includes(o),
                                )
                              : workLocations.filter((o) => o !== option),
                          )
                        }
                      />
                    }
                    label={option}
                  />
                ))}
              </Box>
              <Box display="flex" alignItems="center" flexWrap="wrap">
                <Typography
                  variant="body2"
                  component="span"
                  style={{ display: 'flex', marginRight: 8 }}
                >
                  <CustomTooltip
                    titleHeader="Employment type"
                    bodyText="Only vacancies of the ticked types are compared. Vacancies with no employment type stated always show too."
                    placement="bottom"
                    maxWidth={320}
                    useDefaultUnderline
                  >
                    Employment type
                  </CustomTooltip>
                  :
                </Typography>
                {employmentTypeOptions.map((option) => (
                  <FormControlLabel
                    key={option}
                    style={{ marginLeft: 8 }}
                    control={
                      <Checkbox
                        size="small"
                        color="primary"
                        checked={employmentTypes.includes(option)}
                        // keep at least one, same as work arrangement
                        disabled={
                          employmentTypes.length === 1 &&
                          employmentTypes.includes(option)
                        }
                        onChange={(e) =>
                          setEmploymentTypes(
                            e.target.checked
                              ? employmentTypeOptions.filter(
                                  (o) =>
                                    o === option || employmentTypes.includes(o),
                                )
                              : employmentTypes.filter((o) => o !== option),
                          )
                        }
                      />
                    }
                    label={option}
                  />
                ))}
              </Box>
            </div>
            <Box mt={1} display="flex" alignItems="center" flexWrap="wrap">
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    color="primary"
                    checked={considerSalary}
                    onChange={(e) => setConsiderSalary(e.target.checked)}
                  />
                }
                label={
                  <CustomTooltip
                    titleHeader="Consider salary"
                    bodyText="Compares each vacancy's yearly pay with the candidate's expected salary, or their current salary when no expectation is on the ATS. Vacancies paying more than 10% and at least €5,000 less still show, lower down and marked. Vacancies with no stated or hourly pay, or that pay more, are not affected."
                    placement="bottom"
                    maxWidth={320}
                    useDefaultUnderline
                  >
                    Consider salary (10% leeway)
                  </CustomTooltip>
                }
              />
              <Button
                size="small"
                color="primary"
                onClick={() => setShowMoreOptions((x) => !x)}
                endIcon={
                  <ExpandMoreIcon
                    style={{
                      transform: showMoreOptions ? 'rotate(180deg)' : undefined,
                    }}
                  />
                }
              >
                {moreOptionsLabel}
              </Button>
            </Box>
            <Collapse in={showMoreOptions}>
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      color="primary"
                      checked={publishedOnly}
                      onChange={(e) => setPublishedOnly(e.target.checked)}
                    />
                  }
                  label={
                    <CustomTooltip
                      titleHeader="Published vacancies only"
                      bodyText="Shows only vacancies that are live on the Konnekt website. Leave it unticked to include unpublished open vacancies too, which you can still put candidates forward for."
                      placement="bottom"
                      maxWidth={320}
                      useDefaultUnderline
                    >
                      Published vacancies only
                    </CustomTooltip>
                  }
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      color="primary"
                      checked={considerJobFocus}
                      onChange={(e) => setConsiderJobFocus(e.target.checked)}
                    />
                  }
                  label="Favour candidate's preferred PJFs"
                />
              </Box>
            </Collapse>

            {search.kind === 'idle' && (
              <EmptyView>
                Search for a candidate, or paste their URL or ID, to begin.
              </EmptyView>
            )}

            {search.kind === 'loading' && (
              <Box className={`${classes.zone} ${classes.loadingRow}`}>
                <CircularProgress size={18} />
                <Typography variant="body2">
                  Reading the candidate and finding the best vacancies…
                </Typography>
              </Box>
            )}

            {search.kind === 'error' && (
              <Alert severity={errorSeverity} className={classes.zone}>
                <span>{search.message}</span>
                {search.help && (
                  <Typography
                    variant="body2"
                    style={{ marginTop: 6, whiteSpace: 'pre-line' }}
                  >
                    {search.help}
                  </Typography>
                )}
                {search.code === 'target_profile_thin' && (
                  <Box mt={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      onClick={() => runSearch(true)}
                    >
                      Search anyway
                    </Button>
                  </Box>
                )}
              </Alert>
            )}

            {search.kind === 'loaded' && (
              <>
                <Box className={classes.zone}>
                  <TargetCandidateCard candidate={search.candidate} />
                </Box>

                <div ref={resultsRef} className={classes.zone}>
                  <Typography className={classes.sectionHeader}>
                    Best vacancies
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {resultsSummary}
                  </Typography>

                  {search.alreadyApplied.length > 0 && (
                    <Box mt={0.5}>
                      <Button
                        size="small"
                        color="primary"
                        onClick={() => setShowApplied((x) => !x)}
                        endIcon={
                          <ExpandMoreIcon
                            style={{
                              transform: showApplied
                                ? 'rotate(180deg)'
                                : undefined,
                            }}
                          />
                        }
                      >
                        Excluded {search.alreadyApplied.length}{' '}
                        {search.alreadyApplied.length === 1 ? 'role' : 'roles'}{' '}
                        already applied to
                      </Button>
                      <Collapse in={showApplied}>
                        <Box pl={1}>
                          {search.alreadyApplied.map((applied) => (
                            <Typography
                              key={applied.vacancy_id}
                              variant="body2"
                              color="textSecondary"
                            >
                              {applied.title || `Vacancy ${applied.vacancy_id}`}
                              : {applied.status || 'status unknown'}
                              {applied.closed ? ' (closed)' : ''}
                            </Typography>
                          ))}
                        </Box>
                      </Collapse>
                    </Box>
                  )}

                  {rerankFailed && (
                    <Alert severity="error" style={{ marginTop: 8 }}>
                      Something went wrong, so the order is rough and the match
                      notes are missing. Searching again usually fixes it.
                      {search.rerankError && (
                        <Typography
                          variant="caption"
                          style={{ display: 'block', fontFamily: 'monospace' }}
                        >
                          {search.rerankError}
                        </Typography>
                      )}
                    </Alert>
                  )}
                  {/* one warning: most paying below usually means the candidate's salary is above the market */}
                  {(weakSet || mostPayBelow) && (
                    <Alert severity="warning" style={{ marginTop: 8 }}>
                      {weakSet && <strong>No strong fits found. </strong>}
                      {mostPayBelow && search.salaryComparedTo != null ? (
                        <>
                          Most of these pay below the candidate's{' '}
                          {toMonetary(search.salaryComparedTo)}. Keep this in
                          mind when reviewing results and giving feedback.
                        </>
                      ) : (
                        'Treat these as leads.'
                      )}
                    </Alert>
                  )}

                  {search.results.length === 0 && (
                    <EmptyView>No open vacancies matched.</EmptyView>
                  )}
                  {search.results.length > 0 && (
                    <Box mt={2}>
                      <Box className={classes.results}>
                        {visible.map((result) => (
                          <VacancyResultCard
                            key={result.vacancy_id}
                            rank={search.results.indexOf(result) + 1}
                            result={result}
                            feedback={feedback[result.vacancy_id]}
                            onFeedbackChange={(next) =>
                              handleFeedbackChange(result.vacancy_id, next)
                            }
                            onOpen={() => handleResultOpen(result.vacancy_id)}
                          />
                        ))}
                      </Box>
                      {hiddenCount > 0 && (
                        <Box className={classes.showMoreRow}>
                          <Button
                            variant="outlined"
                            size="small"
                            color="primary"
                            endIcon={<ExpandMoreIcon />}
                            onClick={() => setShowAll(true)}
                          >
                            Show {hiddenCount} more{' '}
                            {hiddenCount === 1 ? 'vacancy' : 'vacancies'}
                          </Button>
                        </Box>
                      )}
                    </Box>
                  )}

                  {search.hiddenRatedBad.length > 0 && (
                    <Box mt={2}>
                      <Button
                        size="small"
                        color="primary"
                        onClick={() => setShowRatedBad((x) => !x)}
                        endIcon={
                          <ExpandMoreIcon
                            style={{
                              transform: showRatedBad
                                ? 'rotate(180deg)'
                                : undefined,
                            }}
                          />
                        }
                      >
                        {showRatedBad ? 'Hide' : 'Show'}{' '}
                        {search.hiddenRatedBad.length}{' '}
                        {search.hiddenRatedBad.length === 1
                          ? 'vacancy'
                          : 'vacancies'}{' '}
                        you marked bad before
                      </Button>
                      <Collapse in={showRatedBad}>
                        <Typography variant="body2" color="textSecondary">
                          Left out of the ranking. Undo the rating on a card to
                          bring it back in your next search.
                        </Typography>
                        <Box className={classes.results} mt={1}>
                          {search.hiddenRatedBad.map((result, i) => (
                            <VacancyResultCard
                              key={result.vacancy_id}
                              rank={i + 1}
                              result={result}
                              feedback={feedback[result.vacancy_id]}
                              onFeedbackChange={(next) =>
                                handleFeedbackChange(result.vacancy_id, next)
                              }
                            />
                          ))}
                        </Box>
                      </Collapse>
                    </Box>
                  )}
                </div>
              </>
            )}
          </Box>
        </Card>
      </Grid>
      <MatcherTutorial
        steps={guideSteps}
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
      />
    </Grid>
  );
};

export default CandidateVacanciesView;
